import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { useUserStore } from '@/configs/store';
import type { EventAttendee } from '@/features/calendarView/types';
import { useTimeLogStore } from '@/pages/TimesheetsPage/model';
import { YandexUser } from '@/types/yandex';

import { TimeLogForm } from './TimeLogForm';
import { DataValues } from './types';
import { getInitialUsers } from './utils';

type AddLogProps = {
  open: boolean;
  onSubmit: (data: DataValues) => Promise<void>;
  onClose: () => void;
  day?: DateTime | null;
  hasPreselectedDate?: boolean;
  taskTitle?: string;
  taskKey?: string;
  isTaskSelected?: boolean;
  isSingleEntry?: boolean;
  initialStartTime?: string;
  initialDuration?: string;
  initialComment?: string;
  calendarAttendees?: EventAttendee[];
  forSelfOnly?: boolean;
  companionPanel?: React.ReactNode;
  onUsersChange?: (users: YandexUser[]) => void;
  onTaskKeyChange?: (taskKey: string | null) => void;
  autoShowCompanion?: boolean;
};

const AddLog: React.FC<AddLogProps> = observer(
  ({
    open,
    onClose,
    day,
    onSubmit,
    hasPreselectedDate,
    taskTitle,
    taskKey,
    isTaskSelected,
    isSingleEntry,
    initialStartTime,
    initialDuration,
    initialComment,
    calendarAttendees,
    forSelfOnly,
    companionPanel,
    onUsersChange,
    onTaskKeyChange,
    autoShowCompanion,
  }) => {
    const { user, isAdmin } = useUserStore();
    const {
      usersFilterModel: { selectedUserId, selectedUser, myUserId },
    } = useTimeLogStore();
    const currentDate = DateTime.local().setLocale('ru').toISODate();
    const pickDate = day?.toISODate();

    const usersWithMyself = getInitialUsers({
      user,
      myUserId,
      isAdmin,
      selectedUserId,
      selectedUser,
      calendarAttendees,
      forSelfOnly,
    });

    const startDate = pickDate ?? currentDate ?? '';

    const initialValues = {
      taskKey: taskKey ?? '',
      taskLabel: taskKey && taskTitle ? `${taskKey} — ${taskTitle}` : '',
      comment: initialComment ?? '',
      users: usersWithMyself,
      dateEntries: [
        { startDate, duration: initialDuration ?? '', startTime: initialStartTime ?? '12:00' },
      ],
    };

    const handleSubmit = async (data: DataValues) => {
      await onSubmit(data);
    };

    return (
      <TimeLogForm
        open={open}
        onClose={onClose}
        formValues={initialValues}
        onSubmit={handleSubmit}
        hasPreselectedDate={hasPreselectedDate}
        isTaskSelected={isTaskSelected}
        isSingleEntry={isSingleEntry}
        companionPanel={companionPanel}
        onUsersChange={onUsersChange}
        onTaskKeyChange={onTaskKeyChange}
        autoShowCompanion={autoShowCompanion}
      />
    );
  },
);

export { AddLog };
